import { Injectable, Injector } from '@angular/core';
import { Observable, of, BehaviorSubject, ReplaySubject } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { ThemeService, Theme } from '@awp/theme';
import { ContentService, ContentType, ContentParser } from '@awp/content';
import { wrapIntoObservable, isNullOrEmpty, trimSlash } from '@awp/core/utils';
import { AppConfig } from '@awp/core/config';

import { ClientService } from './client.service';
import { HalResponse } from './content-hal.service';

@Injectable({
  providedIn: 'root'
})
export class ThemeHalService extends ThemeService {

  _loaded = new BehaviorSubject<boolean>(false);
  _changes = new ReplaySubject<string>(1);

  currentTheme: Theme;
  themeId: string;
  loaded = this._loaded.asObservable();
  changes = this._changes.asObservable();

  constructor(
    private clientService: ClientService,
    private injector: Injector) {
    super();
  }

  verifyTheme(themeId: string): Observable<boolean> {
    if (themeId === this.themeId) {
      return of(true);
    }

    const result = this.clientService.getClient(themeId);

    return wrapIntoObservable(result).pipe(
        map(p => p != null)
      );
  }

  setTheme(themeId: string): void {
    const savedThemeId = sessionStorage.getItem('themeId');

    if (themeId !== this.themeId) {
      this.themeId = themeId;
      this.clientService.setClient(this.themeId);

      const contentService = this.injector.get(ContentService);
      if (contentService != null) {
        contentService.setClient(this.themeId);
      } else {
        console.warn('Content Service is not defined');
      }

      this._themeId.next(themeId);
      if (isNullOrEmpty(savedThemeId) || savedThemeId !== themeId) {
        sessionStorage.setItem('themeId', this.themeId);
        this._changes.next(themeId);
      }
    }
  }

  getTheme(): Observable<Theme> {
    if (this.currentTheme != null) {
      return of(this.currentTheme);
    }

    const contentService = this.injector.get(ContentService) as ContentService;
    const contentParser = this.injector.get(ContentParser) as ContentParser;
    const appConfig = this.injector.get(AppConfig);

    return contentService.getContent('root')
      .pipe(
        map((p: HalResponse) => {
          let cssUrl;

          if (p != null && p.theme != null && p.theme.type === ContentType.Css) {
            cssUrl = p.theme.href;
          }

          return {
            themeId: this.themeId,
            url: {
              themeUrl: trimSlash([trimSlash(appConfig.BASE_HREF), this.themeId].join('/')),
              cssUrl
            },
            pageTitle: contentParser.interpolate(p.pageTitle, p._embedded.properties),
            favIcon: p.favIcon,
            properties: p._embedded.properties
          } as Theme;
        }),
        shareReplay(1)
      );
  }

  reset(): void {
    this._loaded.next(false);
  }

  complete(): void {
    this._loaded.next(true);
  }

}
